import {
  ArrowLeft,
  Check,
  Clock,
  Coins,
  Copy,
  LogOut,
  Music,
  Share2,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSound } from 'context/SoundProvider';
import { useAuth } from 'context/AuthProvider';
import { battleUuidLabel } from 'lib/format-battle';
import { copyPrivateRoomCode, sharePrivateRoomInvite } from 'lib/private-room-invite';
import HeaderPlayerName from 'components/HeaderPlayerName';
import GameLogo from 'components/GameLogo';
import HeaderSettingsPopover from 'components/desktop/HeaderSettingsPopover';

export interface TurnStatus {
  label: string;
  seconds: number | null;
  urgent: boolean;
  isMyTurn: boolean;
}

interface DesktopHeaderProps {
  roomName: string;
  roomCode?: string;
  variantCode?: string;
  battleUuid: string;
  bootAmount: number;
  potAmount: number;
  playersCount: number;
  maxPlayers?: number;
  roundNumber: number;
  turnStatus?: TurnStatus;
  balanceLabel?: string;
  compactLandscape?: boolean;
  compactPortrait?: boolean;
  /** History back — leaves table session via page handler */
  onBack: () => void;
  /** Explicit leave table (defaults to onBack) */
  onLeave?: () => void;
  /** Open wallet without leaving the table */
  onOpenWallet?: () => void;
}

export default function DesktopHeader({
  roomName,
  roomCode,
  variantCode,
  battleUuid,
  roundNumber,
  turnStatus,
  balanceLabel,
  compactLandscape = false,
  compactPortrait = false,
  onBack,
  onLeave,
  onOpenWallet,
}: DesktopHeaderProps) {
  const { user } = useAuth();
  const { muted, musicEnabled, toggleMute, toggleMusic, unlock } = useSound();
  const leaveHandler = onLeave ?? onBack;
  const [codeCopied, setCodeCopied] = useState(false);
  const [codeHint, setCodeHint] = useState('');
  const codeHintTimerRef = useRef<number | null>(null);

  const clearCodeHint = useCallback(() => {
    if (codeHintTimerRef.current != null) {
      window.clearTimeout(codeHintTimerRef.current);
      codeHintTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearCodeHint(), [clearCodeHint]);

  const flashCodeHint = (text: string) => {
    setCodeHint(text);
    clearCodeHint();
    codeHintTimerRef.current = window.setTimeout(() => setCodeHint(''), 2200);
  };

  const onCopyRoomCode = async () => {
    if (!roomCode) return;
    const ok = await copyPrivateRoomCode(roomCode);
    if (ok) {
      setCodeCopied(true);
      clearCodeHint();
      codeHintTimerRef.current = window.setTimeout(() => setCodeCopied(false), 2000);
    } else {
      flashCodeHint('Copy failed');
    }
  };

  const onShareRoomCode = async () => {
    if (!roomCode) return;
    const result = await sharePrivateRoomInvite({ roomCode, variantCode, tableName: roomName });
    if (result === 'shared') flashCodeHint('Invite sent');
    else if (result === 'copied') flashCodeHint('Invite copied');
    else flashCodeHint('Share unavailable');
  };

  const privateCode = roomCode?.trim().toUpperCase();

  const hasBattle = Boolean(battleUuid.trim());
  const battleFull = battleUuidLabel(battleUuid, false);
  const battleMetaTitle = hasBattle
    ? roundNumber > 0
      ? `Battle: ${battleFull} - Round ${roundNumber}`
      : `Battle: ${battleFull}`
    : 'Waiting for hand';

  const battleMetaContent = hasBattle ? (
    <>
      <span className="tp-header-battle-prefix text-white/45">Battle:</span>{' '}
      <span className="tp-header-battle-id text-tp-gold/90">{battleFull}</span>
      {roundNumber > 0 ? (
        <span className="tp-header-battle-round-suffix font-semibold text-white/70">
          {` - Round ${roundNumber}`}
        </span>
      ) : null}
    </>
  ) : (
    'Waiting for hand'
  );

  const showBattleMeta = hasBattle;
  const showPortraitUserRow = compactPortrait;
  const showLandscapeInlineHeader = compactLandscape;
  const showLeaveIconBtn = !compactPortrait && !compactLandscape;
  const showLeaveTextBtn = !compactPortrait && !compactLandscape;

  const headerActions = (
    <>
      {showLeaveTextBtn && (
        <button
          type="button"
          onClick={leaveHandler}
          className="tp-leave-btn hidden min-[640px]:flex items-center gap-1.5 rounded-xl"
          aria-label="Leave table"
        >
          <LogOut size={14} />
          <span>Leave</span>
        </button>
      )}
      {turnStatus && turnStatus.seconds != null && (
        <div
          className={`tp-header-turn-mobile hidden items-center gap-1 rounded-lg px-2 py-1 ${turnStatus.urgent ? 'tp-header-turn-urgent' : ''}`}
        >
          <Clock size={12} className={turnStatus.urgent ? 'text-red-400' : 'text-tp-gold'} />
          <span className="text-[10px] font-bold tabular-nums text-white">{turnStatus.seconds}s</span>
        </div>
      )}
      {showLeaveIconBtn && (
        <button
          type="button"
          onClick={leaveHandler}
          className={`tp-leave-btn tp-leave-btn-icon-only tp-header-leave-icon-btn flex items-center justify-center rounded-xl ${compactLandscape ? 'hidden' : compactPortrait ? 'flex' : 'min-[640px]:hidden'}`}
          aria-label="Leave table"
        >
          <LogOut size={16} />
        </button>
      )}
      <button
        type="button"
        onClick={() => {
          unlock();
          toggleMute();
        }}
        className="tp-glass-btn tp-header-icon-btn flex items-center justify-center rounded-xl"
        aria-label="Sound"
      >
        {muted ? <VolumeX size={17} /> : <Volume2 size={17} />}
      </button>
      {!compactPortrait && !compactLandscape && (
        <button
          type="button"
          onClick={() => {
            unlock();
            toggleMusic();
          }}
          className={`tp-glass-btn tp-header-icon-btn hidden items-center justify-center rounded-xl min-[640px]:flex ${musicEnabled ? 'text-tp-gold ring-1 ring-tp-gold/40' : ''}`}
          aria-label={musicEnabled ? 'Turn music off' : 'Turn music on'}
        >
          <Music size={17} />
        </button>
      )}
      {!compactPortrait && !compactLandscape && <HeaderSettingsPopover />}
    </>
  );

  return (
    <header
      className={[
        'tp-desktop-header relative z-50 flex shrink-0 flex-col',
        compactLandscape ? 'tp-desktop-header-landscape-compact' : '',
        compactPortrait ? 'tp-desktop-header-portrait-compact' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="tp-header-main-row">
        <div className={`tp-header-left ${showLandscapeInlineHeader ? 'tp-header-left-landscape' : ''}`}>
          <GameLogo compact className="tp-header-game-logo flex shrink-0" />
          <button
            type="button"
            onClick={onBack}
            className="tp-glass-btn tp-header-back-btn flex shrink-0 items-center justify-center rounded-xl"
            aria-label="Back"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="tp-header-title-block min-w-0 flex-1">
            <div className="tp-header-title-row flex min-w-0 items-baseline gap-2">
              <h1
                className={[
                  compactPortrait || compactLandscape
                    ? 'tp-header-room-name min-w-0 font-display font-bold tracking-wide text-white'
                    : 'min-w-0 truncate font-display font-bold tracking-wide text-white',
                  compactLandscape
                    ? 'text-[11px] leading-tight'
                    : compactPortrait
                      ? 'text-xs leading-tight'
                      : 'text-xs sm:text-sm lg:text-base',
                ].join(' ')}
                title={roomName}
              >
                {roomName}
              </h1>
              {roundNumber > 0 && !compactLandscape && !compactPortrait ? (
                <span className="tp-header-round-badge hidden shrink-0 rounded-md bg-white/10 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-white/70 lg:inline">
                  R{roundNumber}
                </span>
              ) : null}
            </div>
            {!compactLandscape && !compactPortrait && !hasBattle && (
              <p className="tp-header-round-hint truncate text-[10px] text-white/45">
                Waiting for hand
              </p>
            )}
            {privateCode ? (
              <div className="tp-header-private-code-row mt-0.5 flex min-w-0 flex-wrap items-center gap-1.5">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-white/35">Code</span>
                <span className="font-mono text-[11px] font-bold tracking-[0.22em] text-tp-gold">{privateCode}</span>
                <button
                  type="button"
                  onClick={() => void onCopyRoomCode()}
                  className="tp-header-room-code-btn"
                  aria-label="Copy room code"
                  title="Copy room code"
                >
                  {codeCopied ? <Check size={12} /> : <Copy size={12} />}
                </button>
                <button
                  type="button"
                  onClick={() => void onShareRoomCode()}
                  className="tp-header-room-code-btn"
                  aria-label="Share room invite"
                  title="Share invite"
                >
                  <Share2 size={12} />
                </button>
                {codeHint ? <span className="text-[10px] text-white/45">{codeHint}</span> : null}
              </div>
            ) : null}
            {!compactLandscape && !compactPortrait && hasBattle && roundNumber === 0 && (
              <p className="tp-header-round-hint truncate text-[10px] text-white/45">
                Hand in progress
              </p>
            )}
          </div>
        </div>

        <div className={`tp-header-right ${showLandscapeInlineHeader ? 'tp-header-right-landscape' : ''}`}>
          {!compactPortrait && !compactLandscape && user && (
            <HeaderPlayerName username={user.username} compact className="tp-header-player" />
          )}
          {!compactPortrait && !compactLandscape && balanceLabel && (
            <button
              type="button"
              onClick={onOpenWallet}
              disabled={!onOpenWallet}
              className={`tp-header-balance hidden min-[640px]:flex items-center gap-1 rounded-lg ${onOpenWallet ? 'cursor-pointer transition hover:bg-white/5' : ''}`}
              aria-label={onOpenWallet ? 'Open wallet' : undefined}
            >
              <Coins size={14} className="tp-header-balance-icon shrink-0 text-tp-gold" />
              <span className="min-w-0 max-w-[5.5rem] truncate font-display text-[10px] font-bold tabular-nums text-tp-gold sm:max-w-[6.5rem] sm:text-xs lg:max-w-none">
                {balanceLabel}
              </span>
            </button>
          )}
          {showLandscapeInlineHeader && user ? (
            <HeaderPlayerName
              username={user.username}
              forceShowName
              className="tp-header-player tp-header-player-landscape tp-header-player-landscape-inline shrink-0"
            />
          ) : null}
          {showLandscapeInlineHeader && balanceLabel ? (
            <button
              type="button"
              onClick={onOpenWallet}
              disabled={!onOpenWallet}
              className={`tp-header-balance tp-header-balance-landscape flex shrink-0 items-center gap-1 rounded-lg ${onOpenWallet ? 'cursor-pointer' : ''}`}
              aria-label={onOpenWallet ? 'Open wallet' : undefined}
            >
              <Coins size={12} className="tp-header-balance-icon shrink-0 text-tp-gold" />
              <span className="min-w-0 truncate font-display text-[10px] font-bold tabular-nums text-tp-gold">
                {balanceLabel}
              </span>
            </button>
          ) : null}
          {showLandscapeInlineHeader ? (
            <button
              type="button"
              onClick={leaveHandler}
              className="tp-leave-btn tp-header-landscape-leave flex shrink-0 items-center gap-1 rounded-lg"
              aria-label="Leave table"
            >
              <LogOut size={12} />
              <span>Leave</span>
            </button>
          ) : null}
          {showLandscapeInlineHeader ? (
            <div className="tp-header-landscape-icon-actions">{headerActions}</div>
          ) : (
            headerActions
          )}
        </div>
      </div>

      {showPortraitUserRow && (
        <div className="tp-header-portrait-user-row">
          {user ? (
            <HeaderPlayerName username={user.username} forceShowName className="tp-header-player tp-header-player-portrait" />
          ) : (
            <span className="min-w-0 flex-1" />
          )}
          <div className="tp-header-portrait-user-actions">
            {balanceLabel ? (
              <button
                type="button"
                onClick={onOpenWallet}
                disabled={!onOpenWallet}
                className={`tp-header-balance tp-header-balance-portrait flex items-center gap-1.5 rounded-lg ${onOpenWallet ? 'cursor-pointer' : ''}`}
                aria-label={onOpenWallet ? 'Open wallet' : undefined}
              >
                <Coins size={15} className="tp-header-balance-icon shrink-0 text-tp-gold" />
                <span className="min-w-0 truncate font-display text-[11px] font-bold tabular-nums text-tp-gold sm:text-xs">
                  {balanceLabel}
                </span>
              </button>
            ) : null}
            <button
              type="button"
              onClick={leaveHandler}
              className="tp-leave-btn tp-header-portrait-leave flex shrink-0 items-center gap-1 rounded-lg"
              aria-label="Leave table"
            >
              <LogOut size={13} />
              <span>Leave</span>
            </button>
          </div>
        </div>
      )}

      {showBattleMeta && (
        <div
          className={[
            'tp-header-battle-row',
            compactLandscape ? 'tp-header-battle-row-landscape' : '',
            compactPortrait ? 'tp-header-battle-row-portrait' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <p
            className="tp-header-battle-meta font-mono text-[10px] text-tp-gold/70 sm:text-[11px] lg:text-xs min-w-0 shrink truncate"
            title={battleMetaTitle}
          >
            {battleMetaContent}
          </p>
        </div>
      )}
    </header>
  );
}
